import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Plus, Trash2, Edit2, Upload, Package, Sparkles, ArrowLeft, Image as ImageIcon, FileText, Check, X, Lightbulb, ThumbsUp, Download, TestTube } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminTools() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [tools, setTools] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topicModal, setTopicModal] = useState(null); // {id?, name, description, tint}
  const [toolModal, setToolModal] = useState(null); // form state
  const [uploadingFor, setUploadingFor] = useState(null); // toolId
  const [showSuggestions, setShowSuggestions] = useState(true);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/');
    load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const [{ data: tp }, { data: tl }, { data: sg }] = await Promise.all([
        api.get('/admin/tool-topics'),
        api.get('/admin/tools'),
        api.get('/admin/tool-suggestions').catch(() => ({ data: [] })),
      ]);
      setTopics(tp);
      setTools(tl);
      setSuggestions(sg);
    } catch (_) {
      toast.error('Could not load Sacred Tools');
    } finally {
      setLoading(false);
    }
  };

  const updateSuggestion = async (sid, updates) => {
    try {
      await api.put(`/admin/tool-suggestions/${sid}`, updates);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };
  const removeSuggestion = async (sid) => {
    if (!window.confirm('Delete this suggestion?')) return;
    try {
      await api.delete(`/admin/tool-suggestions/${sid}`);
      await load();
    } catch (_) { toast.error('Could not delete'); }
  };

  // ---------- Topic actions ----------
  const openNewTopic = () => setTopicModal({ name: '', description: '', tint: '' });
  const openEditTopic = (t) => setTopicModal({ ...t });
  // Turn any axios error into a human-readable string that also surfaces
  // network / HTTP-status info (production errors were being hidden as a
  // flat "Could not save" toast — this exposes the real cause).
  const describeError = (err, fallback) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (detail) return `${status ? status + ': ' : ''}${formatApiErrorDetail(detail)}`;
    if (status) return `${status}: ${fallback}`;
    if (err?.message) return `${fallback} — ${err.message}`;
    return fallback;
  };

  const saveTopic = async () => {
    if (!topicModal?.name?.trim()) return toast.error('Give the topic a name');
    try {
      if (topicModal.id) {
        await api.put(`/admin/tool-topics/${topicModal.id}`, { name: topicModal.name, description: topicModal.description, tint: topicModal.tint || undefined });
      } else {
        await api.post('/admin/tool-topics', { name: topicModal.name, description: topicModal.description || '', tint: topicModal.tint || undefined });
      }
      setTopicModal(null);
      await load();
      toast.success('Saved');
    } catch (err) {
      console.error('saveTopic failed:', err);
      toast.error(describeError(err, 'Could not save'));
    }
  };
  const removeTopic = async (t) => {
    if (!window.confirm(`Delete topic "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/tool-topics/${t.id}`);
      await load();
    } catch (err) {
      console.error('removeTopic failed:', err);
      toast.error(describeError(err, 'Could not delete'));
    }
  };

  // ---------- Tool actions ----------
  const openNewTool = (topicId) => setToolModal({
    topic_id: topicId, title: '', description: '',
    price_cents: 500, member_price_cents: '',
    published: false,
    pendingFile: null, pendingCover: null,
    lastError: null,
  });
  const openEditTool = (t) => setToolModal({ ...t, member_price_cents: t.member_price_cents ?? '', pendingFile: null, pendingCover: null, lastError: null });
  const saveTool = async () => {
    if (!toolModal?.title?.trim()) return toast.error('Give the tool a title');
    if (!toolModal?.description?.trim()) return toast.error('Add a short description');
    if (!toolModal?.price_cents || toolModal.price_cents < 50) return toast.error('Price must be at least $0.50');
    if (!toolModal.id && !toolModal.pendingFile) return toast.error('Attach the downloadable file so customers can receive it');
    setToolModal((prev) => prev ? { ...prev, lastError: null } : prev);
    const body = {
      topic_id: toolModal.topic_id,
      title: toolModal.title,
      description: toolModal.description,
      price_cents: parseInt(toolModal.price_cents, 10),
      member_price_cents: toolModal.member_price_cents === '' ? null : parseInt(toolModal.member_price_cents, 10),
      published: !!toolModal.published,
    };
    try {
      let toolId = toolModal.id;
      if (toolId) {
        await api.put(`/admin/tools/${toolId}`, body);
      } else {
        const { data } = await api.post('/admin/tools', body);
        toolId = data.id;
      }
      // Upload the downloadable file if the admin picked one in the modal.
      // Uses the chunked flow so files over the ~1MB production ingress limit
      // still get through (the previous single-shot POST was blocked on prod).
      if (toolModal.pendingFile) {
        await chunkedUpload(toolId, toolModal.pendingFile, 'file');
      }
      // Upload the cover image if picked
      if (toolModal.pendingCover) {
        await chunkedUpload(toolId, toolModal.pendingCover, 'cover');
      }
      toast.success(toolModal.id ? 'Saved' : 'Tool created and file attached');
      setToolModal(null);
      await load();
    } catch (err) {
      console.error('saveTool failed:', err);
      const msg = describeError(err, 'Could not save');
      const detail = {
        message: msg,
        status: err?.response?.status || null,
        url: err?.config?.url || null,
        method: (err?.config?.method || '').toUpperCase() || null,
        body: err?.response?.data ?? null,
      };
      toast.error(msg);
      setToolModal((prev) => prev ? { ...prev, lastError: detail } : prev);
    }
  };
  const togglePublished = async (t) => {
    try {
      await api.put(`/admin/tools/${t.id}`, { published: !t.published });
      await load();
    } catch (err) {
      console.error('togglePublished failed:', err);
      toast.error(describeError(err, 'Could not update'));
    }
  };
  const removeTool = async (t) => {
    if (!window.confirm(`Delete tool "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/tools/${t.id}`);
      await load();
    } catch (err) {
      console.error('removeTool failed:', err);
      toast.error(describeError(err, 'Could not delete'));
    }
  };
  // Chunked upload — sends the file in ≤700KB pieces to slip past the 1MB
  // ingress body-size limit on production. Falls back to a friendly error toast
  // if any leg of the flow fails.
  const CHUNK_SIZE = 700 * 1024;

  const chunkedUpload = async (toolId, file, kind /* 'file' | 'cover' */) => {
    // 1. Init session
    const { data: init } = await api.post(`/admin/tools/${toolId}/upload/init`, {
      kind,
      filename: file.name,
      content_type: file.type || (kind === 'cover' ? 'image/jpeg' : 'application/octet-stream'),
      total_size: file.size,
    });
    const uploadId = init.upload_id;
    const chunkSize = init.chunk_size || CHUNK_SIZE;
    const totalChunks = Math.ceil(file.size / chunkSize);

    // 2. Stream chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      const form = new FormData();
      form.append('upload_id', uploadId);
      form.append('chunk_index', String(i));
      form.append('chunk', blob, `chunk-${i}`);
      await api.post(`/admin/tools/${toolId}/upload/chunk`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setToolModal((prev) => prev ? { ...prev, uploadProgress: { kind, done: i + 1, total: totalChunks } } : prev);
    }

    // 3. Finish
    const finishForm = new FormData();
    finishForm.append('upload_id', uploadId);
    const { data: done } = await api.post(`/admin/tools/${toolId}/upload/finish`, finishForm, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setToolModal((prev) => prev ? { ...prev, uploadProgress: null } : prev);
    return done;
  };

  const uploadFile = async (toolId, file) => {
    try {
      await chunkedUpload(toolId, file, 'file');
      toast.success('File uploaded');
      await load();
    } catch (err) {
      console.error('uploadFile failed:', err);
      setToolModal((prev) => prev ? { ...prev, uploadProgress: null } : prev);
      toast.error(describeError(err, 'Upload failed'));
    }
  };
  const uploadCover = async (toolId, file) => {
    try {
      await chunkedUpload(toolId, file, 'cover');
      toast.success('Cover uploaded');
      await load();
    } catch (err) {
      console.error('uploadCover failed:', err);
      setToolModal((prev) => prev ? { ...prev, uploadProgress: null } : prev);
      toast.error(describeError(err, 'Upload failed'));
    }
  };

  // Admin-only quick download to verify what customers will receive after purchase.
  const testDownload = async (toolId) => {
    try {
      const { data } = await api.post(`/tools/${toolId}/download-url`);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download');
    }
  };

  // Simulate a full paid customer purchase to test the entire post-checkout
  // flow (My Toolkit, download button, receipt) without spending real money.
  const testPurchase = async (toolId) => {
    try {
      const { data } = await api.post(`/admin/tools/${toolId}/test-purchase`);
      toast.success(data.message || 'Test purchase recorded');
      // Small delay so the toast is visible before navigating away
      setTimeout(() => { window.location.href = data.checkout_url; }, 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create test purchase');
    }
  };

  const resetTestPurchase = async (toolId) => {
    if (!window.confirm('Remove your admin test purchase for this tool so you can run the flow again?')) return;
    try {
      const { data } = await api.delete(`/admin/tools/${toolId}/test-purchase`);
      const n = (data.removed_purchases || 0) + (data.removed_transactions || 0);
      toast.success(n > 0 ? 'Test purchase cleared — you can run the flow again.' : 'No test purchase found to clear.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset');
    }
  };

  const toolsByTopic = (topicId) => tools.filter(t => t.topic_id === topicId);

  const [diagnostics, setDiagnostics] = useState(null);
  const [runningDiag, setRunningDiag] = useState(false);
  const runDiagnostics = async () => {
    setRunningDiag(true);
    setDiagnostics(null);
    try {
      const { data } = await api.get('/admin/diagnostics/tools');
      setDiagnostics(data);
    } catch (err) {
      console.error('diagnostics failed:', err);
      setDiagnostics({ _error: describeError(err, 'Diagnostics failed') });
    } finally {
      setRunningDiag(false);
    }
  };

  if (loading) return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-24 text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }} /></div>
      <Footer />
    </div>
  );

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="admin-tools-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Sacred Tools</h1>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Create topics and add downloadable tools. Publish to release to everyone.</p>
          </div>
          <button onClick={openNewTopic} className="px-5 py-2.5 rounded-full flex items-center gap-2 hover:-translate-y-0.5 transition"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700 }}
            data-testid="admin-tools-new-topic">
            <Plus className="w-4 h-4" /> New topic
          </button>
        </div>

        {/* Diagnostics panel — surfaces production storage / config issues on-screen */}
        <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8' }} data-testid="admin-tools-diagnostics">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', letterSpacing: '0.02em' }}>
                Diagnostics
              </h3>
              <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                Tap this if Save, Upload, or Download misbehaves — it prints exactly what's wrong.
              </p>
            </div>
            <button
              onClick={runDiagnostics}
              disabled={runningDiag}
              className="px-4 py-2 rounded-full text-sm disabled:opacity-60"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5B7C99', color: '#FFFFFF', fontWeight: 700 }}
              data-testid="admin-tools-run-diagnostics"
            >
              {runningDiag ? 'Running…' : 'Run diagnostics'}
            </button>
          </div>
          {diagnostics && (
            <pre
              className="mt-4 p-3 rounded-xl text-xs whitespace-pre-wrap break-words"
              style={{ backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8', color: '#2C2724', maxHeight: '360px', overflowY: 'auto' }}
              data-testid="admin-tools-diagnostics-output"
            >
{JSON.stringify(diagnostics, null, 2)}
            </pre>
          )}
        </div>

        {suggestions.length > 0 && (
          <div className="mb-8 rounded-3xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: '1px solid #5B7C9930' }} data-testid="admin-suggestions-panel">
            <button onClick={() => setShowSuggestions(!showSuggestions)} className="w-full p-4 flex items-center gap-3 transition" style={{ backgroundColor: '#F0F5FA' }}>
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#5B7C9925', color: '#5B7C99' }}>
                <Lightbulb className="w-5 h-5" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Reader Suggestions</h3>
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{suggestions.length} idea{suggestions.length === 1 ? '' : 's'} · {suggestions.filter(s => s.status === 'new').length} new</p>
              </div>
              {showSuggestions ? <X className="w-4 h-4" style={{ color: '#8C867D' }} /> : <ArrowLeft className="w-4 h-4 rotate-[-90deg]" style={{ color: '#8C867D' }} />}
            </button>
            {showSuggestions && (
              <ul className="divide-y" style={{ borderColor: '#F0EBE0' }}>
                {suggestions.map(s => (
                  <li key={s.id} className="p-4 flex items-start gap-4 flex-wrap" data-testid={`admin-suggestion-${s.id}`}>
                    <div className="flex items-center gap-1 px-3 py-2 rounded-xl" style={{ backgroundColor: '#F0F5FA', color: '#5B7C99', minWidth: '52px' }}>
                      <ThumbsUp className="w-3.5 h-3.5" />
                      <span className="text-xs font-bold">{s.upvote_count}</span>
                    </div>
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="text-sm font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{s.title}</h4>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8', color: '#595553', fontFamily: 'Nunito, sans-serif' }}>${(s.suggested_price_cents / 100).toFixed(2)}</span>
                      </div>
                      <p className="text-xs mb-1" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.6 }}>{s.description}</p>
                      <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        {s.submitter_name}{s.submitter_email ? ` · ${s.submitter_email}` : ''} · {new Date(s.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <select value={s.status} onChange={(e) => updateSuggestion(s.id, { status: e.target.value })}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#F3EFE7', color: '#2C2724', border: '1px solid #E6E2D8' }}
                        data-testid={`admin-suggestion-status-${s.id}`}>
                        <option value="new">New idea</option>
                        <option value="considering">Considering</option>
                        <option value="created">Created ✓</option>
                        <option value="declined">Not this one</option>
                      </select>
                      <button onClick={() => removeSuggestion(s.id)} className="p-1.5 rounded-lg opacity-60 hover:opacity-100 transition" title="Delete"><Trash2 className="w-3.5 h-3.5" style={{ color: '#8C5E5E' }} /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {topics.length === 0 ? (
          <div className="p-14 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }}>
            <Package className="w-12 h-12 mx-auto mb-3" style={{ color: '#E8C265' }} />
            <p className="mb-4" style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>No topics yet. Create your first — e.g. "Conflict Resolution".</p>
            <button onClick={openNewTopic} className="px-6 py-2.5 rounded-full inline-flex items-center gap-2"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFFFFF', fontWeight: 700 }}>
              <Plus className="w-4 h-4" /> Create your first topic
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {topics.map(topic => (
              <div key={topic.id} className="rounded-3xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0' }} data-testid={`admin-topic-${topic.id}`}>
                <div className="p-5 flex items-center justify-between flex-wrap gap-3" style={{ backgroundColor: `${topic.tint}10`, borderBottom: '1px solid #F0EBE0' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${topic.tint}25`, color: topic.tint }}>
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{topic.name}</h2>
                      <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{topic.published_count} published · {topic.tool_count} total</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openNewTool(topic.id)} className="px-3 py-1.5 rounded-full text-xs flex items-center gap-1 hover:-translate-y-0.5 transition"
                      style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFFFFF', fontWeight: 700 }}
                      data-testid={`admin-add-tool-${topic.id}`}>
                      <Plus className="w-3.5 h-3.5" /> Add tool
                    </button>
                    <button onClick={() => openEditTopic(topic)} className="p-1.5 rounded-lg opacity-60 hover:opacity-100 transition" title="Edit"><Edit2 className="w-3.5 h-3.5" style={{ color: '#8C867D' }} /></button>
                    <button onClick={() => removeTopic(topic)} className="p-1.5 rounded-lg opacity-60 hover:opacity-100 transition" title="Delete" data-testid={`admin-delete-topic-${topic.id}`}><Trash2 className="w-3.5 h-3.5" style={{ color: '#8C5E5E' }} /></button>
                  </div>
                </div>
                <div className="p-5">
                  {toolsByTopic(topic.id).length === 0 ? (
                    <p className="text-sm text-center py-6" style={{ fontFamily: 'Lora, serif', color: '#8C867D', fontStyle: 'italic' }}>No tools yet. Add one to release it to the shop.</p>
                  ) : (
                    <ul className="space-y-3">
                      {toolsByTopic(topic.id).map(t => (
                        <li key={t.id} className="p-4 rounded-2xl flex items-start gap-4 flex-wrap" style={{ backgroundColor: '#FDFBF7', border: '1px solid #F0EBE0' }} data-testid={`admin-tool-${t.id}`}>
                          <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.cover_url ? `url(${t.cover_url}) center/cover` : `${topic.tint}20` }}>
                            {!t.cover_url && <Package className="w-6 h-6" style={{ color: topic.tint }} />}
                          </div>
                          <div className="flex-1 min-w-[220px]">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="text-sm font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{t.title}</h3>
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: t.published ? '#78A18E' : '#F3EFE7', color: t.published ? '#FFFFFF' : '#8C867D', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>
                                {t.published ? 'Published' : 'Draft'}
                              </span>
                              {!t.file_path && <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E8C265', color: '#2C2A29', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>No file yet</span>}
                            </div>
                            <p className="text-xs mb-2 line-clamp-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{t.description}</p>
                            <div className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                              ${(t.price_cents / 100).toFixed(2)}{t.member_price_cents ? ` · Members $${(t.member_price_cents / 100).toFixed(2)}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <label className="px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1 cursor-pointer transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5B7C99', color: '#FFFFFF', fontWeight: 600 }} data-testid={`admin-upload-file-${t.id}`}>
                              <Upload className="w-3 h-3" /> File
                              <input type="file" hidden onChange={(e) => e.target.files[0] && uploadFile(t.id, e.target.files[0])} />
                            </label>
                            <label className="px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1 cursor-pointer transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#F3EFE7', color: '#2C2724', fontWeight: 600, border: '1px solid #E6E2D8' }}>
                              <ImageIcon className="w-3 h-3" /> Cover
                              <input type="file" hidden accept="image/*" onChange={(e) => e.target.files[0] && uploadCover(t.id, e.target.files[0])} />
                            </label>
                            {t.file_path && (
                              <button onClick={() => testDownload(t.id)} className="px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1 transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 600 }} title="Download to verify the file customers will receive" data-testid={`admin-test-download-${t.id}`}>
                                <Download className="w-3 h-3" /> Test
                              </button>
                            )}
                            {t.file_path && (
                              <button onClick={() => testPurchase(t.id)} className="px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1 transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#B77BB0', color: '#FFFFFF', fontWeight: 600 }} title="Simulate a paying customer without spending money" data-testid={`admin-test-purchase-${t.id}`}>
                                <TestTube className="w-3 h-3" /> Buy
                              </button>
                            )}
                            <button onClick={() => togglePublished(t)} className="px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1 transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: t.published ? '#E8C265' : '#78A18E', color: t.published ? '#2C2A29' : '#FFFFFF', fontWeight: 700 }}>
                              {t.published ? <><X className="w-3 h-3" /> Unpublish</> : <><Check className="w-3 h-3" /> Publish</>}
                            </button>
                            <button onClick={() => openEditTool(t)} className="p-1.5 rounded-lg opacity-60 hover:opacity-100 transition" title="Edit"><Edit2 className="w-3.5 h-3.5" style={{ color: '#8C867D' }} /></button>
                            <button onClick={() => removeTool(t)} className="p-1.5 rounded-lg opacity-60 hover:opacity-100 transition" title="Delete"><Trash2 className="w-3.5 h-3.5" style={{ color: '#8C5E5E' }} /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Topic modal */}
      {topicModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44,39,36,0.6)' }} data-testid="admin-topic-modal">
          <div className="w-full max-w-md p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{topicModal.id ? 'Edit topic' : 'New topic'}</h3>
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Name</label>
            <input value={topicModal.name} onChange={(e) => setTopicModal({ ...topicModal, name: e.target.value })} maxLength={120} placeholder="e.g., Conflict Resolution"
              className="w-full px-4 py-2.5 rounded-xl border mb-4"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8' }}
              data-testid="admin-topic-name" />
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Description <span className="text-xs" style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span></label>
            <textarea value={topicModal.description || ''} onChange={(e) => setTopicModal({ ...topicModal, description: e.target.value })} rows={3} maxLength={500} placeholder="A short line shown under the topic name"
              className="w-full px-4 py-2.5 rounded-xl border mb-5"
              style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8' }} />
            <div className="flex justify-end gap-3">
              <button onClick={() => setTopicModal(null)} className="px-4 py-2 rounded-full text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#595553', fontWeight: 600 }}>Cancel</button>
              <button onClick={saveTopic} className="px-5 py-2 rounded-full text-sm transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700 }} data-testid="admin-topic-save">Save topic</button>
            </div>
          </div>
        </div>
      )}

      {/* Tool modal */}
      {toolModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44,39,36,0.6)' }} data-testid="admin-tool-modal">
          <div className="w-full max-w-xl p-6 rounded-3xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{toolModal.id ? 'Edit tool' : 'New tool'}</h3>
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Title</label>
            <input value={toolModal.title} onChange={(e) => setToolModal({ ...toolModal, title: e.target.value })} maxLength={180} placeholder="e.g., 7-Day Marriage Reset Workbook"
              className="w-full px-4 py-2.5 rounded-xl border mb-4" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8' }}
              data-testid="admin-tool-title" />
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Description</label>
            <textarea value={toolModal.description} onChange={(e) => setToolModal({ ...toolModal, description: e.target.value })} rows={5} maxLength={4000} placeholder="What's inside, how to use it, who it's for"
              className="w-full px-4 py-2.5 rounded-xl border mb-4" style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8' }}
              data-testid="admin-tool-description" />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Public price ($)</label>
                <input type="number" step="0.01" min="0.50" max="15" value={(toolModal.price_cents / 100).toFixed(2)}
                  onChange={(e) => setToolModal({ ...toolModal, price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                  className="w-full px-4 py-2.5 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8' }}
                  data-testid="admin-tool-price" />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Sanctuary price ($) <span className="text-xs" style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span></label>
                <input type="number" step="0.01" min="0.50" max="15" placeholder="Same as public if empty"
                  value={toolModal.member_price_cents === '' ? '' : (toolModal.member_price_cents / 100).toFixed(2)}
                  onChange={(e) => setToolModal({ ...toolModal, member_price_cents: e.target.value === '' ? '' : Math.round(parseFloat(e.target.value || 0) * 100) })}
                  className="w-full px-4 py-2.5 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8' }} />
              </div>
            </div>
            <label className="flex items-center gap-2 mb-6 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>
              <input type="checkbox" checked={!!toolModal.published} onChange={(e) => setToolModal({ ...toolModal, published: e.target.checked })} data-testid="admin-tool-published" />
              <span>Publish to the public shop now</span>
            </label>

            {/* Downloadable file (the actual product) */}
            <div className="mb-4 p-4 rounded-2xl" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA7440' }}>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#7A5C1E' }}>
                Downloadable file <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <p className="text-xs mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#7A5C3C' }}>
                This is what customers receive after purchase. Any file type is fine (PDF, DOCX, ZIP, MP3, MP4). Max 500 MB.
              </p>
              {toolModal.pendingFile ? (
                <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: '#F0F9F4', border: '1px solid #78A18E40' }}>
                  <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#5C715E' }} />
                  <span className="text-sm truncate flex-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#365B48' }}>
                    {toolModal.pendingFile.name} ({(toolModal.pendingFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                  <button onClick={() => setToolModal({ ...toolModal, pendingFile: null })} className="text-xs px-2 py-1 rounded" style={{ color: '#DC2626', fontWeight: 600 }}>
                    Remove
                  </button>
                </div>
              ) : toolModal.file_path ? (
                <div className="flex items-center gap-2 mb-2 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C715E' }}>
                  <Check className="w-4 h-4" /> File already attached — pick a new file below to replace it
                </div>
              ) : null}
              <label className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm cursor-pointer transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFFFFF', fontWeight: 600 }} data-testid="admin-tool-file-picker">
                <Upload className="w-4 h-4" />
                {toolModal.pendingFile ? 'Change file' : toolModal.file_path ? 'Replace downloadable file' : 'Choose downloadable file'}
                <input
                  type="file"
                  hidden
                  onChange={(e) => e.target.files[0] && setToolModal({ ...toolModal, pendingFile: e.target.files[0] })}
                />
              </label>
            </div>

            {/* Cover image (visual for the shop) */}
            <div className="mb-6 p-4 rounded-2xl" style={{ backgroundColor: '#F5F7F4', border: '1px solid #78A18E30' }}>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#4B6B5C' }}>
                Cover image <span className="text-xs font-normal" style={{ color: '#8C867D' }}>(optional)</span>
              </label>
              <p className="text-xs mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C6E5F' }}>
                Shown as the thumbnail in the Sacred Tools shop. JPG or PNG, square works best.
              </p>
              {toolModal.pendingCover ? (
                <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: '#F0F9F4', border: '1px solid #78A18E40' }}>
                  <ImageIcon className="w-4 h-4 flex-shrink-0" style={{ color: '#5C715E' }} />
                  <span className="text-sm truncate flex-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#365B48' }}>
                    {toolModal.pendingCover.name}
                  </span>
                  <button onClick={() => setToolModal({ ...toolModal, pendingCover: null })} className="text-xs px-2 py-1 rounded" style={{ color: '#DC2626', fontWeight: 600 }}>
                    Remove
                  </button>
                </div>
              ) : toolModal.cover_url ? (
                <div className="flex items-center gap-2 mb-2 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C715E' }}>
                  <Check className="w-4 h-4" /> Cover already set — pick a new image to replace it
                </div>
              ) : null}
              <label className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm cursor-pointer transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 600 }} data-testid="admin-tool-cover-picker">
                <ImageIcon className="w-4 h-4" />
                {toolModal.pendingCover ? 'Change cover' : toolModal.cover_url ? 'Replace cover' : 'Choose cover image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && setToolModal({ ...toolModal, pendingCover: e.target.files[0] })}
                />
              </label>
            </div>

            {toolModal.lastError && (
              <div
                className="mb-4 p-4 rounded-2xl"
                style={{ backgroundColor: '#FDECEA', border: '1px solid #E57A44', color: '#7A1F0F', fontFamily: 'Manrope, sans-serif' }}
                data-testid="admin-tool-error-banner"
              >
                <p className="text-sm font-semibold mb-2">Save failed — please share this with support:</p>
                <pre className="text-xs whitespace-pre-wrap break-words" style={{ margin: 0 }}>
{JSON.stringify(toolModal.lastError, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={() => setToolModal(null)} className="px-4 py-2 rounded-full text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#595553', fontWeight: 600 }}>Cancel</button>
              <button onClick={saveTool} className="px-5 py-2 rounded-full text-sm transition hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFFFFF', fontWeight: 700 }} data-testid="admin-tool-save">
                {toolModal.id ? 'Save changes' : 'Create tool'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
